/**
 * Cart Size Selector
 * Handles variant changes for line items directly in the cart
 */

class CartSizeSelector extends HTMLElement {
  constructor() {
    super();
    this.isUpdating = false;
  }
  
  connectedCallback() {
    setTimeout(() => {
      this.init();
    }, 0);
  }
  
  init() {
    this.select = this.querySelector('.cart-size-selector__select');
    
    if (!this.select) {
      return;
    }
    
    this.lineKey = this.select.dataset.lineKey;
    this.currentVariantId = this.select.dataset.currentVariantId;
    
    this.select.addEventListener('change', this.handleSizeChange.bind(this));
  }
  
  async handleSizeChange(event) {
    if (this.isUpdating) {
      return;
    }
    
    const newVariantId = event.target.value;
    const oldVariantId = this.currentVariantId;
    
    if (newVariantId === oldVariantId) {
      return;
    }
    
    const lineItem = this.closest('tr, line-item');
    const quantityInput = lineItem?.querySelector('input[type="number"][data-line-key]');
    const currentQuantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;
    
    const selectedOption = event.target.options[event.target.selectedIndex];
    const inventory = parseInt(selectedOption.dataset.inventory, 10);
    const inventoryManagement = selectedOption.dataset.inventoryManagement;
    const inventoryPolicy = selectedOption.dataset.inventoryPolicy;
    
    
    if (inventoryManagement === 'shopify' && inventoryPolicy === 'deny') {
      if (inventory < currentQuantity) {
        console.error('Insufficient inventory:', inventory, '<', currentQuantity);
        this.showError(`Not enough stock. Only ${inventory} available for this size.`);
        
        this.select.value = oldVariantId;
        return;
      }
    }
    
    try {
      this.isUpdating = true;
      this.setLoadingState(true);
      
      const isCartPage = window.themeVariables?.settings?.pageType === 'cart';
      
      if (isCartPage) {
        await this.updateCartVariant(this.lineKey, newVariantId, currentQuantity);
        this.currentVariantId = newVariantId;
        this.select.dataset.currentVariantId = newVariantId;
        window.location.reload();
      } else {
        await this.updateCartVariantAjax(this.lineKey, newVariantId, currentQuantity, lineItem);
        this.currentVariantId = newVariantId;
        this.select.dataset.currentVariantId = newVariantId;
        this.setLoadingState(false);
        this.isUpdating = false;
      }
      
    } catch (error) {
      console.error('Error updating cart variant:', error);
      
      this.select.value = oldVariantId;
      
      this.showError(error.message || 'Failed to update size. Please try again.');
      
      this.setLoadingState(false);
      this.isUpdating = false;
    }
  }
  
  async updateCartVariantAjax(lineKey, newVariantId, quantity, lineItemElement) {
    if (!window.Shopify || !window.Shopify.routes) {
      throw new Error('Shopify routes not available');
    }
    
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
    
    lineItemElement?.dispatchEvent(new CustomEvent('line-item:will-change', { 
      bubbles: true, 
      detail: { targetQuantity: quantity } 
    }));
    
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { 
      bubbles: true, 
      detail: { sections: sectionsToBundle } 
    }));
    
    try {
      const removeResponse = await fetch(`${Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
          sections: sectionsToBundle.join(',')
        })
      });
      
      if (!removeResponse.ok) {
        throw new Error('Failed to remove old variant');
      }
      
      const addResponse = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: newVariantId,
            quantity: quantity
          }],
          sections: sectionsToBundle.join(',')
        })
      });
      
      if (!addResponse.ok) {
        await this.restoreOldVariant(lineKey, quantity);
        throw new Error('The selected size is not available');
      }
      
      const cartContent = await addResponse.json();
      
      const newLineItem = cartContent.items ? cartContent.items.find(item => item.variant_id == newVariantId) : null;
      
      lineItemElement?.dispatchEvent(new CustomEvent('line-item:change', {
        bubbles: true,
        detail: {
          quantity: newLineItem ? newLineItem.quantity : 0,
          cart: cartContent
        }
      }));
      
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'line-item:change',
          cart: cartContent
        }
      }));
      
      return cartContent;
      
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }
  

  async restoreOldVariant(lineKey, quantity) {
    try {
      await fetch(`${Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: quantity
        })
      });
    } catch (error) {
      console.error('Failed to restore old variant:', error);
    }
  }
  
  async updateCartVariant(lineKey, newVariantId, quantity) {
    if (!window.Shopify || !window.Shopify.routes) {
      throw new Error('Shopify routes not available');
    }
    
    
    const removeResponse = await fetch(`${Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: lineKey,
        quantity: 0
      })
    });
    
    if (!removeResponse.ok) {
      throw new Error('Failed to remove old variant');
    }
    
    const cartAfterRemove = await removeResponse.json();
    
    const addResponse = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        items: [{
          id: newVariantId,
          quantity: quantity
        }]
      })
    });
    
    if (!addResponse.ok) {
      let errorMessage = 'The selected size is not available';
      try {
        const errorData = await addResponse.json();
        console.error('Add to cart error:', errorData);
        
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.description) {
          errorMessage = errorData.description;
        }
      } catch (e) {
        console.error('Could not parse error response:', e);
      }
      
      try {
        const restoreResponse = await fetch(`${Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: lineKey,
            quantity: quantity
          })
        });
        
        if (!restoreResponse.ok) {
          console.error('Failed to restore old item');
        }
      } catch (restoreError) {
        console.error('Failed to restore cart:', restoreError);
      }
      
      throw new Error(errorMessage);
    }
    
    const cartAfterAdd = await addResponse.json();
    
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: {
        cart: cartAfterAdd,
        baseEvent: 'variant:change'
      }
    }));
    
    return cartAfterAdd;
  }
  
  setLoadingState(isLoading) {
    if (isLoading) {
      this.select.disabled = true;
      this.select.classList.add('is-loading');
      this.classList.add('is-updating');
    } else {
      this.select.disabled = false;
      this.select.classList.remove('is-loading');
      this.classList.remove('is-updating');
    }
  }
  
  showError(message) {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'cart-size-selector__error text-error text-xs';
    errorDiv.textContent = message;
    errorDiv.style.marginTop = '0.25rem';
    
    const existingError = this.querySelector('.cart-size-selector__error');
    if (existingError) {
      existingError.remove();
    }
    
    this.appendChild(errorDiv);
    
    setTimeout(() => {
      errorDiv.remove();
    }, 5000);
  }
}

if ('customElements' in window) {
  customElements.define('cart-size-selector', CartSizeSelector);
} else {
  console.error('Custom elements not supported');
}

